/**
 * Cross-Tab Authentication Synchronisation Utility (Enterprise Standard 14)
 * Ensures that logging out from ANY tab or portal instantly invalidates sessions,
 * clears backend auth cookies, and logs out all open tabs across the entire application
 * via BroadcastChannel('musb_cross_tab_auth_sync') and localStorage storage-event fallbacks.
 */

const PRIMARY_CHANNEL = 'musb_cross_tab_auth_sync';
const LEGACY_CHANNEL = 'musb_cross_tab_auth';
const LOGOUT_KEY = 'musb_logout_event';

// Complete registry of storage keys to clear on logout
const ALL_AUTH_KEYS = [
  'admin_token',
  'super_admin_token',
  'admin_user',
  'patient_token',
  'patient_user',
  'token',
  'user',
  'portal_user',
  'doctor_token',
  'doctor_user',
  'facility_token',
  'facility_user',
  'nonprofit_token',
  'nonprofit_user',
  'phleb_token',
  'phleb_user',
  'lab_token',
  'lab_user',
  'hub_token',
  'hub_user',
  'diag_token',
  'diag_user',
  'research_token',
  'res_token',
  'research_user',
  'res_user',
  'employer_token',
  'employer_user',
  'technician_token',
  'technician_user',
  'staff_token',
  'staff_user',
  'musb_active_lab_id',
  'lab_id',
  'active_lab',
  'selected_lab',
  'login_event',
  'role',
  'user_role',
  'is_staff'
];

let primaryChannel = null;
let legacyChannel = null;

try {
  if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
    primaryChannel = new BroadcastChannel(PRIMARY_CHANNEL);
    legacyChannel = new BroadcastChannel(LEGACY_CHANNEL);
  }
} catch (e) {
  // BroadcastChannel unavailable or restricted
}

/**
 * Clears all auth items from localStorage, sessionStorage, and calls backend unified logout
 */
export function clearAllAuthTokens() {
  if (typeof window === 'undefined') return;

  // 1. Wipe all local storage auth keys
  ALL_AUTH_KEYS.forEach((key) => {
    try { localStorage.removeItem(key); } catch (e) {}
  });

  // 2. Wipe entire session storage
  try {
    sessionStorage.clear();
  } catch (e) {}

  // 3. Clear backend HttpOnly cookies across all namespaces
  try {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/logout/', true);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.send(JSON.stringify({}));
  } catch (e) {}
}

/**
 * Broadcasts a logout event to all other open tabs
 */
export function broadcastLogout(source = 'unknown') {
  if (typeof window === 'undefined') return;

  clearAllAuthTokens();

  const payload = {
    type: 'LOGOUT',
    source,
    timestamp: Date.now(),
  };

  // 1. BroadcastChannel (fast, modern browsers)
  if (primaryChannel) {
    try { primaryChannel.postMessage(payload); } catch (e) {}
  }
  if (legacyChannel) {
    try { legacyChannel.postMessage(payload); } catch (e) {}
  }

  // 2. Storage event fallback (fires synchronously on all other tabs)
  try {
    localStorage.setItem(LOGOUT_KEY, JSON.stringify(payload));
    setTimeout(() => {
      try { localStorage.removeItem(LOGOUT_KEY); } catch (_) {}
    }, 500);
  } catch (e) {}

  // 3. Local events for current tab
  window.dispatchEvent(new CustomEvent('local-storage-update', { detail: payload }));
}

/**
 * Listens for logout events from other tabs.
 * Calls `onLogoutCallback(payload)` when another tab logs out.
 * Returns an unsubscribe function.
 */
export function listenCrossTabLogout(onLogoutCallback) {
  if (typeof window === 'undefined') return () => {};

  const handleMessage = (event) => {
    if (event?.data?.type === 'LOGOUT') {
      onLogoutCallback(event.data);
    }
  };

  const handleStorage = (event) => {
    if (event.key === LOGOUT_KEY && event.newValue) {
      try {
        const data = JSON.parse(event.newValue);
        onLogoutCallback(data);
      } catch (e) {
        onLogoutCallback({ type: 'LOGOUT', timestamp: Date.now() });
      }
    } else if (
      event.key &&
      (event.key.endsWith('_token') || event.key === 'token') &&
      !event.newValue
    ) {
      // An auth token was deleted in another tab
      onLogoutCallback({ type: 'LOGOUT', tokenKey: event.key, timestamp: Date.now() });
    }
  };

  if (primaryChannel) {
    primaryChannel.addEventListener('message', handleMessage);
  }
  if (legacyChannel) {
    legacyChannel.addEventListener('message', handleMessage);
  }
  window.addEventListener('storage', handleStorage);

  return () => {
    if (primaryChannel) {
      primaryChannel.removeEventListener('message', handleMessage);
    }
    if (legacyChannel) {
      legacyChannel.removeEventListener('message', handleMessage);
    }
    window.removeEventListener('storage', handleStorage);
  };
}
